<script setup lang="ts">
import type { Api } from '@rialtic/api'
import { useClaimFormContext } from '../composables/fields'
import { useHelpers } from '../composables/helpers'
import { useIsRialticUser } from '../composables/user'
import { useClaimLab } from '../stores/claimLab'
import type { RawInsights } from '../types'
import type { RowSchema } from '../utils/claim-editor'
import { resolveWidget } from '../utils/claim-widgets'
import StatusBar from './StatusBar.vue'

const props = defineProps<{
  sequence: number
  fields: RowSchema[]
  claim?: Api.ProcessedClaimWithInsights
  sequentialIndex: number
  claimLineSequence: Record<number, number>
}>()

const claimLab = useClaimLab()
const isRialticUser = useIsRialticUser()

const emit = defineEmits<{
  'remove-claimline': [payload: { sequence: number; isOtherClaim: boolean }]
}>()

const userPermissions = inject<ComputedRef<string[]>>('user-permissions')

const { getExistingStatus, getTestStatus } = useHelpers(userPermissions)
const { state, isFieldChanged } = useClaimFormContext()

const resolvedSequence = computed(
  () => props.claimLineSequence[props.sequence] || props.sequence,
)

const fhirClaimline = computed(() =>
  props.claim
    ? props.claim.claimLines?.find(
        (line) => line.sequence === resolvedSequence.value,
      )
    : undefined,
)

const isNewClaimLine = computed(
  () => props.sequentialIndex > claimLab.claimLinesCount,
)

const bypassCode = computed(
  () => fhirClaimline.value?.additionalDetails?.bypassCode,
)
const isLineDisabled = computed(
  () =>
    Number(bypassCode.value) === 36 ||
    (props.claim?.bypass_edit_sequence?.[`${props.sequence}`] ?? false),
)

const otherClaimKey = computed(() => `is_other_claim-${props.sequence}`)
state[otherClaimKey.value] ??= false

const isOtherClaim = computed({
  get: () => state[otherClaimKey.value] === true,
  set: (value) => {
    state[otherClaimKey.value] = value
  },
})

const hasEdits = computed(
  () =>
    isOtherClaim.value || props.fields.some(({ name }) => isFieldChanged(name)),
)

const onToggleOtherClaim = (checked: boolean) => {
  if (checked) {
    claimLab.removeNewClaimLineSequence(props.sequence)
    claimLab.addOtherClaimLineSequence(props.sequence)
  } else {
    claimLab.addNewClaimLineSequence(props.sequence)
    claimLab.removeOtherClaimLineSequence(props.sequence)
  }
}
</script>

<template>
  <section
    class="border-default z-0 row-span-full grid grid-rows-subgrid border-r"
  >
    <template
      v-for="{
        name,
        component,
        refDataModel,
        label,
        isEditable,
        editKey,
        class: className,
        dataType,
        ...attrs
      } in fields"
      :key="name"
    >
      <div
        v-if="name.startsWith('line')"
        class="z-1 border-default sticky top-0 flex h-full w-full items-center justify-between border-b p-2 text-xs"
        :class="isLineDisabled ? 'bg-neutral-50 text-neutral-400' : 'bg-white'"
      >
        <div class="flex w-full items-center justify-between gap-1">
          {{ `${isOtherClaim ? 'Other ' : ''}Claim Line ${resolvedSequence}` }}
          <UTooltip
            v-if="isLineDisabled"
            arrow
            text="This claim line will be skipped during processing."
          >
            <UIcon
              name="i-ph-prohibit"
              class="text-primary-300 block size-4 cursor-pointer"
            />
          </UTooltip>
        </div>
        <div class="flex items-center space-x-1">
          <div
            v-if="hasEdits"
            class="bg-primary-300 h-2 w-2 rounded-full"
            data-testid="ClaimLineEditor__editIndicator"
          />
          <UButton
            v-if="isNewClaimLine"
            aria-label="Remove claim line"
            color="neutral"
            icon="i-ph-x-bold"
            size="xs"
            variant="ghost"
            @click="$emit('remove-claimline', { sequence, isOtherClaim })"
          />
        </div>
      </div>
      <div
        v-else-if="name.startsWith('existing_status')"
        class="border-default flex h-full w-full items-center border-b p-2 text-xs"
        :class="isLineDisabled ? 'bg-neutral-50 text-neutral-400' : 'bg-white'"
        :data-testid="`ClaimLineEditor__existingStatus-${sequence}`"
      >
        <USwitch
          v-if="isRialticUser && isNewClaimLine"
          v-model="isOtherClaim"
          aria-label="Toggle other claim line"
          label="Other Claim"
          size="xs"
          :data-testid="`ClaimLineEditor__otherClaimToggle-${sequence}`"
          @update:model-value="onToggleOtherClaim"
        />
        <StatusBar
          v-else-if="fhirClaimline"
          :status="
            getExistingStatus(fhirClaimline.insights as unknown as RawInsights)
          "
        />
        <span v-else class="text-xs">No insights</span>
      </div>
      <div
        v-else-if="name.startsWith('test_status')"
        class="border-default flex h-full w-full items-center border-b p-2 text-xs"
        :class="isLineDisabled ? 'bg-neutral-50 text-neutral-400' : 'bg-white'"
      >
        <StatusBar
          v-if="claimLab.testResult"
          :status="
            getTestStatus(
              fhirClaimline?.insights as unknown as RawInsights,
              claimLab.testResultsBySequence?.get(sequence),
              isOtherClaim ? resolvedSequence : undefined,
            )
          "
        />
        <span v-else class="text-xs">Run test to view</span>
      </div>
      <div
        v-else-if="name.startsWith('bypass_code')"
        class="border-default flex h-full w-full items-center border-b p-2 text-xs"
        :class="isLineDisabled ? 'bg-neutral-50 text-neutral-400' : 'bg-white'"
      >
        <span class="sr-only">Bypass code:</span>
        {{ bypassCode ?? '-' }}
      </div>
      <div
        v-else
        :class="[
          className,
          isEditable
            ? 'bg-elevated block h-full w-full text-xs outline outline-dashed outline-1 outline-neutral-500'
            : 'bg-default text-left text-xs font-normal outline outline-1 outline-neutral-200',
        ]"
      >
        <component
          :is="resolveWidget(component!)"
          :name="name || editKey"
          :attributes="{
            placeholder: attrs.placeholder,
            autocomplete: 'off',
            'data-1p-ignore': true,
            'data-lpignore': true,
            'data-no-autofill': true,
            required: attrs.isRequired && !isLineDisabled,
            readonly: attrs.isReadonly,
            disabled: isLineDisabled,
            class: 'bg-transparent w-full',
          }"
          :label="label"
          :ref-data-model="refDataModel"
          :data-type="dataType"
        />
      </div>
    </template>
  </section>
</template>
